import gql from 'graphql-tag';

const GET_CLIENT_CONTRACT_TEMPLATES = gql`
  query getClientContractTemplates($listInfo: ListInfoInput) {
    getClientContractTemplates(listInfo: $listInfo) {
      templates {
        templateId
        name
        description
        # items {
        #   itemId
        #   subscriptionType
        #   unitPrice
        #   unitType
        #   quantity
        #   effectiveDate
        #   serviceItem {
        #     itemId
        #     name
        #     invoiceDescription
        #     quantityType
        #     roundUpValue
        #     unitPrice
        #     category {
        #       categoryId
        #       name
        #     }
        #   }
        # }
        # customItems {
        #   serviceItem {
        #     itemId
        #     name
        #     invoiceDescription
        #     quantityType
        #     roundUpValue
        #     unitPrice
        #     category {
        #       categoryId
        #       name
        #     }
        #   }
        #   unitPrice
        #   discountRate
        # }
        # blacklistedItems {
        #   itemId
        #   name
        #   invoiceDescription
        #   quantityType
        #   roundUpValue
        #   unitPrice
        #   category {
        #     categoryId
        #     name
        #   }
        # }
      }
      listInfo {
        hasMore
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_CLIENT_CONTRACT_TEMPLATE = gql`
  query getClientContractTemplate($templateId: ID!) {
    getClientContractTemplate(templateId: $templateId) {
      templateId
      name
      description
      items {
        assetView {
          id
        }
        itemId
        # subscriptionType
        frequency {
          unit
          type
          quantity
          recurringMode
          recurringBefore
        }
        unitPrice
        unitType
        quantity
        effectiveDate
        endDate
        blockItemUsedIn
        amount
        carryForwardRemainingBlockHours
        autoRenew
        adjustBlockItemAgainstAllItems
        description
        blockItemAdjustedItems {
          itemId
          name
        }
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
          blockItemUsedIn
          blockItemAdjustedItems {
            itemId
            name
          }
        }
      }
      customItems {
        itemId
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
          blockItemUsedIn
          blockItemAdjustedItems {
            itemId
            name
          }
        }
        discountRate
        unitPrice
      }
      conditionalPricing {
        pricingId
        name
        description
        ruleCondition
        items {
          itemId
          serviceItem {
            itemId
            name
          }
          businessHoursPrice
          afterHoursPrice
        }
      }
      blacklistedItems {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
      }
      sla {
        id
        sla {
          id
          name
        }
        slaOrder
      }
    }
  }
`;

const GET_CLIENT_SUBSCRIPTION_DETAILS = gql`
  query getClientContract($clientId: ID!) {
    getClientContract(clientId: $clientId) {
      contractId
      items {
        itemId
        endDate
        serviceItem {
          itemId
          name
        }
        blockItemAdjustedItems {
          itemId
          name
        }
      }
    }
  }
`;

const GET_CLIENT_CONTRACT = gql`
  query getClientContract($clientId: ID!, $isBillingQueue: Boolean!) {
    getClientContract(clientId: $clientId) {
      contractId
      name
      description
      startDate
      endDate
      discountRate
      hasExpiredItems
      items {
        assetView {
          id
        }
        adjustBlockItemAgainstAllItems
        unitPriceOverridden
        itemId
        # subscriptionType
        frequency {
          unit
          type
          quantity
          recurringMode
          recurringBefore
        }
        unitPrice
        unitType
        quantity
        effectiveDate
        nextBillDate
        billDate @include(if: $isBillingQueue)
        endDate
        amount
        blockItemUsedIn
        carryForwardRemainingBlockHours
        autoRenew
        canEdit
        canEditEndDate
        blockItemBalance
        remindDuration {
          unit
          quantity
        }
        blockItemAdjustedItems {
          itemId
          name
        }
        description
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
          blockItemUsedIn
          blockItemAdjustedItems {
            itemId
            name
          }
          integrationState {
            xero
            quickbooks
            pax8
          }
          integrationAddOn {
            addOnId
            addOn
          }
        }
        sites {
          id
          name
        }
        changes @include(if: $isBillingQueue) {
          changeId
          quantity
          quantityChangeOperation
          effectiveDate
          amount
          proRatedAmount
          generateInvoice
        }
      }
      customItems {
        itemId
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
          blockItemUsedIn
          blockItemAdjustedItems {
            itemId
            name
          }
        }
        unitPrice
        discountRate
        afterHoursUnitPrice
      }
      conditionalPricing {
        pricingId
        name
        description
        ruleCondition
        items {
          itemId
          serviceItem {
            itemId
            name
          }
          businessHoursPrice
          afterHoursPrice
        }
      }
      blacklistedItems {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
      }
      sla {
        id
        sla {
          id
          name
        }
        slaOrder
      }
      pax8LastSyncStatus #OperationStatus
      pax8LastSyncTime
    }
  }
`;

const GET_CLIENT_REVENUE_METRICS = gql`
  query getClientRevenueMetrics($input: ClientIdentifierInput!) {
    getClientRevenueMetrics(input: $input) {
      monthlyRecurringRevenue
      revenue
      profit
    }
  }
`;

const GET_CONTRACT_INTEGRATION_STATUS = gql`
  query getClientContract($clientId: ID!) {
    getClientContract(clientId: $clientId) {
      contractId
      pax8LastSyncStatus #OperationStatus
      pax8LastSyncTime
    }
  }
`;

// enum OperationStatus {
//   PROGRESS
//   SUCCESS
//   FAILURE
// }

const GET_INVOICE_LINE_ITEM_PLACEHOLDERS = gql`
  query getInvoiceLineItemPlaceholders($module: String!) {
    getInvoiceLineItemPlaceholders(module: $module)
  }
`;

const GET_CONDITIONAL_PRICING_META = gql`
  query {
    getConditionalPricingMeta
  }
`;

const GET_NEXT_BILL_DATE = gql`
  query($input: GetNextBillDateInput!) {
    getNextBillDate(input: $input)
  }
`;

const GET_INTEGRATION_ADDON_CONTRACT_ITEM = gql`
  query getIntegrationAddOnContractItem(
    $input: GetIntegrationAddonContractItemInput
  ) {
    getIntegrationAddOnContractItem(input: $input) {
      unitPriceOverridden
      itemId
      # subscriptionType
      unitPrice
      unitType
      quantity
      effectiveDate
      billDate
      endDate
      amount
      blockItemUsedIn
      carryForwardRemainingBlockHours
      autoRenew
      canEdit
      canEditEndDate
      blockItemBalance
      blockItemAdjustedItems {
        itemId
        name
      }
      description
      serviceItem {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
        businessHoursUnitPrice
        afterHoursUnitPrice
        useAsWorklogItem
        quantity
        blockItemUsedIn
        blockItemAdjustedItems {
          itemId
          name
        }
        integrationAddOn {
          addOnId
          addOn
        }
        integrationState {
          xero
          quickbooks
          pax8
        }
      }
      sites {
        id
        name
      }
      changes {
        changeId
        quantity
        quantityChangeOperation
        effectiveDate
        amount
        proRatedAmount
        generateInvoice
      }
    }
  }
`;

const GET_PRORATED_AMOUNT = gql`
  query($itemInput: GetProratedAmountInput) {
    getProratedAmount(input: $itemInput)
  }
`;

export {
  GET_CLIENT_CONTRACT_TEMPLATES,
  GET_CLIENT_CONTRACT_TEMPLATE,
  GET_CLIENT_CONTRACT,
  GET_INVOICE_LINE_ITEM_PLACEHOLDERS,
  GET_CONDITIONAL_PRICING_META,
  GET_CONTRACT_INTEGRATION_STATUS,
  GET_CLIENT_SUBSCRIPTION_DETAILS,
  GET_INTEGRATION_ADDON_CONTRACT_ITEM,
  GET_NEXT_BILL_DATE,
  GET_CLIENT_REVENUE_METRICS,
  GET_PRORATED_AMOUNT
};
